import { useState, useEffect } from "react";
import { useNavigate, useLocation } from 'react-router-dom';
import '../styles/newrecruiter.css';
import '../styles/sections.css';
import '../styles/questionbank.css';
import '../styles/superadmin.css';
import logo from '../assets/logo.png';
import API from '../api/api';
import ProfileDropdown from '../components/ProfileDropdown';
import Toast from '../components/Toast';
import { clearDraft, loadDraft, saveDraft } from '../utils/draftStorage';
import { getStoredUserForRole } from '../utils/authStorage';

const EMPTY_SECTION_FORM = { name: '', desc: '' };
const getSectionDraftKey = (id) => id ? `sections:edit:${id}` : 'sections:create';

const IconHome = ({ active }) => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g clipPath="url(#clip0_home)">
      <path d="M9.99638 13.9949V8.6635C9.99638 8.48675 9.92617 8.31724 9.80119 8.19226C9.67621 8.06728 9.5067 7.99707 9.32995 7.99707H6.66423C6.48748 7.99707 6.31798 8.06728 6.193 8.19226C6.06802 8.31724 5.9978 8.48675 5.9978 8.6635V13.9949" stroke={active ? 'white' : '#26282B'} strokeWidth="1.33286" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M1.99927 6.6641C1.99922 6.47021 2.04148 6.27865 2.12308 6.10278C2.20469 5.9269 2.32368 5.77095 2.47177 5.6458L7.13678 1.64788C7.37735 1.44456 7.68215 1.33301 7.99714 1.33301C8.31212 1.33301 8.61693 1.44456 8.8575 1.64788L13.5225 5.6458C13.6706 5.77095 13.7896 5.9269 13.8712 6.10278C13.9528 6.27865 13.9951 6.47021 13.995 6.6641V12.662C13.995 13.0155 13.8546 13.3545 13.6046 13.6044C13.3547 13.8544 13.0156 13.9948 12.6621 13.9948H3.33213C2.97863 13.9948 2.63961 13.8544 2.38965 13.6044C2.13969 13.3545 1.99927 13.0155 1.99927 12.662V6.6641Z" stroke={active ? 'white' : '#26282B'} strokeWidth="1.33286" strokeLinecap="round" strokeLinejoin="round"/>
    </g>
    <defs>
      <clipPath id="clip0_home">
        <rect width="15.9943" height="15.9943" fill="white"/>
      </clipPath>
    </defs>
  </svg>
);

const IconCandidates = ({ active }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path 
      d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" 
      stroke={active ? 'white' : '#26282B'} 
      strokeWidth="1.66667" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
    <circle 
      cx="9" 
      cy="7" 
      r="4" 
      stroke={active ? 'white' : '#26282B'} 
      strokeWidth="1.66667" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
    <path 
      d="M23 21v-2a4 4 0 0 0-3-3.87" 
      stroke={active ? 'white' : '#26282B'} 
      strokeWidth="1.66667" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
    <path 
      d="M16 3.13a4 4 0 0 1 0 7.75" 
      stroke={active ? 'white' : '#26282B'} 
      strokeWidth="1.66667" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
  </svg>
);

const IconSections = ({ active }) => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2.5 5H17.5" stroke={active ? 'white' : '#26282B'} strokeWidth="1.66667" strokeLinecap="round"/>
    <path d="M2.5 10H17.5" stroke={active ? 'white' : '#26282B'} strokeWidth="1.66667" strokeLinecap="round"/>
    <path d="M2.5 15H17.5" stroke={active ? 'white' : '#26282B'} strokeWidth="1.66667" strokeLinecap="round"/>
  </svg>
);

const IconQuestionBank = ({ active }) => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M10 14.167H10.0083" stroke={active ? 'white' : '#26282B'} strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M12.4999 1.66699H4.99992C4.55789 1.66699 4.13397 1.84259 3.82141 2.15515C3.50885 2.46771 3.33325 2.89163 3.33325 3.33366V16.667C3.33325 17.109 3.50885 17.5329 3.82141 17.8455C4.13397 18.1581 4.55789 18.3337 4.99992 18.3337H14.9999C15.4419 18.3337 15.8659 18.1581 16.1784 17.8455C16.491 17.5329 16.6666 17.109 16.6666 16.667V5.83366L12.4999 1.66699Z" stroke={active ? 'white' : '#26282B'} strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M7.58325 7.49982C7.78323 6.94867 8.17056 6.48535 8.67753 6.19084C9.18449 5.89633 9.77884 5.78937 10.3567 5.88865C10.9345 5.98793 11.4591 6.28715 11.8387 6.73399C12.2182 7.18083 12.4287 7.74686 12.4333 8.33315C12.4333 9.99982 9.93325 10.8331 9.93325 10.8331" stroke={active ? 'white' : '#26282B'} strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const IconExamManagement = ({ active }) => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12.5001 1.66699H7.50008C7.03984 1.66699 6.66675 2.04009 6.66675 2.50033V4.16699C6.66675 4.62723 7.03984 5.00033 7.50008 5.00033H12.5001C12.9603 5.00033 13.3334 4.62723 13.3334 4.16699V2.50033C13.3334 2.04009 12.9603 1.66699 12.5001 1.66699Z" stroke={active ? 'white' : '#26282B'} strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M13.3333 3.33301H14.9999C15.4419 3.33301 15.8659 3.5086 16.1784 3.82116C16.491 4.13372 16.6666 4.55765 16.6666 4.99967V16.6663C16.6666 17.1084 16.491 17.5323 16.1784 17.8449C15.8659 18.1574 15.4419 18.333 14.9999 18.333H4.99992C4.55789 18.333 4.13397 18.1574 3.82141 17.8449C3.50885 17.5323 3.33325 17.1084 3.33325 16.6663V4.99967C3.33325 4.55765 3.50885 4.13372 3.82141 3.82116C4.13397 3.5086 4.55789 3.33301 4.99992 3.33301H6.66659" stroke={active ? 'white' : '#26282B'} strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M10 9.16699H13.3333" stroke={active ? 'white' : '#26282B'} strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M10 13.333H13.3333" stroke={active ? 'white' : '#26282B'} strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M6.66675 9.16699H6.67508" stroke={active ? 'white' : '#26282B'} strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M6.66675 13.333H6.67508" stroke={active ? 'white' : '#26282B'} strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const IconAnalytics = ({ active }) => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2.5 2.5V15.8333C2.5 16.2754 2.67559 16.6993 2.98816 17.0118C3.30072 17.3244 3.72464 17.5 4.16667 17.5H17.5" stroke={active ? 'white' : '#26282B'} strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M15 14.1667V7.5" stroke={active ? 'white' : '#26282B'} strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M10.8333 14.167V4.16699" stroke={active ? 'white' : '#26282B'} strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M6.66675 14.167V11.667" stroke={active ? 'white' : '#26282B'} strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const IconDrive = ({ active }) => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M3.333 6.667h13.334M5 3.333h10c.92 0 1.667.746 1.667 1.667v10c0 .92-.746 1.667-1.667 1.667H5A1.667 1.667 0 0 1 3.333 15V5c0-.92.746-1.667 1.667-1.667Z" stroke={active ? 'white' : '#26282B'} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/><path d="M7 10h.01M10 10h3M7 13h.01M10 13h3" stroke={active ? 'white' : '#26282B'} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>
);

const sidebarItems = [
  { label: 'Home',            Icon: IconHome,           path: '/recruiter' },
  { label: 'Drives',          Icon: IconDrive,          path: '/drives' },
  { label: 'Exam Management', Icon: IconExamManagement, path: '/exam-management' },
  { label: 'Candidates',      Icon: IconCandidates,     path: '/candidates' },
  { label: 'Sections',        Icon: IconSections,       path: '/sections' },
  { label: 'Question Bank',   Icon: IconQuestionBank,   path: '/question-bank' },
  { label: 'Analytics',       Icon: IconAnalytics,      path: '/analytics' },
];

const adminSidebarItems = [
  { label: 'Dashboard',     Icon: IconHome,           path: '/superadmin' },
  { label: 'Recruiters',    Icon: IconCandidates,     path: '/superadmin?tab=Recruiters' },
  { label: 'Sections',      Icon: IconSections,       path: '/superadmin/sections' },
  { label: 'Question Bank', Icon: IconQuestionBank,   path: '/superadmin/questionbank' },
  { label: 'Analytics',     Icon: IconAnalytics,      path: '/superadmin?tab=Analytics' },
  { label: 'Logs',          Icon: IconExamManagement, path: '/superadmin?tab=Logs' },
  { label: 'SMTP Settings', Icon: IconExamManagement, path: '/superadmin?tab=SMTP Settings' },
];

export default function Sections() {
  const [addCandidateHover, setAddCandidateHover] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [sections, setSections] = useState([]);
  const [form, setForm] = useState({ name: '', desc: '' });
  const [sectionToDelete, setSectionToDelete] = useState(null);
  const [deleteError, setDeleteError] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    API.get('/sections')
      .then(res => {
        setSections(res.data.sections.map(s => ({
          id: s.id,
          name: s.name,
          desc: s.description || 'Add a description',
          type: s.is_compulsory ? 'Compulsory' : 'Optional',
          questions: s.question_count?.toString().padStart(2, '0') || '00',
          isSystem: s.is_compulsory,
        })));
      })
      .catch(() => alert('Failed to load sections'));
  }, []);
  const navigate = useNavigate();
  const location = useLocation();

  const isSuperAdminView = location.pathname.startsWith('/superadmin');
  const storedUser = getStoredUserForRole(isSuperAdminView ? 'superadmin' : 'recruiter');
  const canEdit = isSuperAdminView || storedUser.role === 'senior_recruiter';
  const canDelete = isSuperAdminView;

  useEffect(() => {
    if (!showModal) return;
    saveDraft(getSectionDraftKey(editingId), form);
  }, [editingId, form, showModal]);

  const handleSave = async () => {
    if (!form.name.trim()) return;
    try {
      const isEditing = Boolean(editingId);
      if (editingId) {
        const res = await API.put(`/sections/${editingId}`, { name: form.name, description: form.desc });
        const s = res.data.section;
        setSections(prev => prev.map(sec => sec.id === editingId
          ? { ...sec, name: s.name, desc: s.description || '-' }
          : sec
        ));
      } else {
        const res = await API.post('/sections', { name: form.name, description: form.desc });
        const s = res.data.section;
        setSections(prev => [...prev, { id: s.id, name: s.name, desc: s.description || 'Add a description', type: s.is_compulsory ? 'Compulsory' : 'Optional', questions: '00', isSystem: s.is_compulsory }]);
      }
      clearDraft(getSectionDraftKey(editingId));
      setForm(EMPTY_SECTION_FORM);
      setEditingId(null);
      setShowModal(false);
      setToast({
        message: isEditing ? 'Section updated successfully.' : 'Section created successfully.',
        type: 'success',
      });
    } catch (err) {
      setToast({
        message: err.response?.data?.message || 'Failed to save section',
        type: 'error',
      });
    }
  };

  const handleEdit = (row) => {
    setEditingId(row.id);
    const defaultForm = { name: row.name, desc: row.desc === 'Add a description' ? '' : row.desc };
    setForm(loadDraft(getSectionDraftKey(row.id), defaultForm));
    setShowModal(true);
  };

  const handleOpenCreate = () => {
    setEditingId(null);
    setForm(loadDraft(getSectionDraftKey(null), EMPTY_SECTION_FORM));
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingId(null);
    setForm(EMPTY_SECTION_FORM);
  };

  const handleCloseModalOverlay = () => {
    setToast({
      message: 'Draft saved. You can continue editing this section later.',
      type: 'success',
    });
    handleCloseModal();
  };

  const handleDelete = async () => {
    if (!sectionToDelete) return;
    setDeleting(true);
    setDeleteError('');
    try {
      await API.delete(`/sections/${sectionToDelete.id}`);
      setSections(prev => prev.filter(s => s.id !== sectionToDelete.id));
      setSectionToDelete(null);
    } catch (err) {
      setDeleteError(err.response?.data?.message || 'Server error. Please try again.');
    } finally {
      setDeleting(false);
    }
  };
  return (
    <div className={isSuperAdminView ? 'sa-page sa-resource-page' : 'nr-page'}>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* ── Navbar ── */}
      <div className="nr-navbar-wrapper">
        <img src={logo} alt="Inteliment Logo" className="nr-navbar-logo-img" />
        <div className="nr-navbar-greeting">
          <h2 className="nr-navbar-greeting-title">{isSuperAdminView ? 'SuperAdmin Portal' : 'Sections'}</h2>
          <p className="nr-navbar-greeting-sub">{isSuperAdminView ? 'Super Admin' : `${sections.length} categories available`}</p>
        </div>
        <div className="nr-navbar-actions">
          {canEdit && (
          <button
            className={`nr-navbar-add-btn ${addCandidateHover ? 'hovered' : ''}`}
            onMouseEnter={() => setAddCandidateHover(true)}
            onMouseLeave={() => setAddCandidateHover(false)}
            onClick={handleOpenCreate}
          >
            <span className="nr-navbar-add-icon">+</span>
            New Section
          </button>
          )}
          <div className="nr-navbar-profile">
            <ProfileDropdown user={storedUser.name || storedUser.email} email={storedUser.email} />
          </div>
        </div>
      </div>

      {/* ── Sidebar ── */}
      <aside className={isSuperAdminView ? 'sa-sidebar' : 'nr-sidebar'}>
        {(isSuperAdminView ? adminSidebarItems : sidebarItems.filter(item => !(storedUser.role === 'junior_recruiter' && item.path === '/question-bank'))).map(({ label, Icon, path }, index) => {
          const targetPath = path;
          const isActive = location.pathname === targetPath.split('?')[0] && (!targetPath.includes('?') || location.search === '?' + targetPath.split('?')[1]);
          return (
            <div
              key={label}
              onClick={() => { 
                if (targetPath) {
                  navigate(targetPath);
                }
              }}
              className={`${isSuperAdminView ? 'sa-sidebar-item' : 'nr-sidebar-item'} ${isActive ? 'active' : ''}`}
              style={isSuperAdminView ? { marginTop: index === 0 ? '60px' : '0' } : { top: `${140 + index * 64}px` }}
            >
              <span className={isSuperAdminView ? 'sa-sidebar-icon' : 'nr-sidebar-icon'}>
                <Icon active={isActive} />
              </span>
              <span className={isSuperAdminView ? undefined : 'nr-sidebar-text'}>{label}</span>
            </div>
          );
        })}
      </aside>

      {/* ── Main ── */}
        <main className={isSuperAdminView ? 'sa-main sa-resource-main' : 'nr-main'}>
          {isSuperAdminView && <h2 className="sa-section-title">Sections</h2>}
          <div className={isSuperAdminView ? 'sa-table-card sa-resource-card' : 'sa-resource-content'}>

            {/* ── Filter Bar ── */}
            <div className="qb-filter-bar">
              {/* Type filter buttons */}
              <div className="sec-type-btn-group">
                {['All', 'Compulsory', 'Optional'].map(t => (
                  <button
                    key={t}
                    className={`sec-type-btn ${typeFilter === t ? 'sec-type-btn--active' : ''}`}
                    onClick={() => { setTypeFilter(t); setCurrentPage(1); }}
                  >{t}</button>
                ))}
              </div>

              {/* Search */}
              <div className="qb-filter-search-box">
                <div className="qb-filter-input-inner">
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}><circle cx="7" cy="7" r="5" stroke="#94A3B8" strokeWidth="1.5"/><path d="M11 11L14 14" stroke="#94A3B8" strokeWidth="1.5" strokeLinecap="round"/></svg>
                  <input
                    type="text"
                    className="qb-filter-search-input"
                    placeholder="Search sections..."
                    value={searchQuery}
                    onChange={e => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                  />
                  {searchQuery && (
                    <button onClick={() => setSearchQuery('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8', fontSize: '16px', lineHeight: 1, padding: 0, flexShrink: 0 }}>×</button>
                  )}
                </div>
              </div>
            </div>

            <div className={`sec-table-container ${!isSuperAdminView ? 'sec-table-container--no-actions' : ''}`}>

                {/* Table Header */}
                <div className="sec-table-header">
                <span className="sec-col-name">Name</span>
                <span className="sec-col-desc">Description</span>
                <span className="sec-col-type">Type</span>
                <span className="sec-col-questions">Questions</span>
                {isSuperAdminView && <span className="sec-col-actions">Actions</span>}
                </div>

                {/* Rows */}
                {(() => {
                  const filtered = sections
                    .filter(row => {
                      const matchSearch = (row.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                                          (row.desc || '').toLowerCase().includes(searchQuery.toLowerCase());
                      const matchType = typeFilter === 'All' || row.type === typeFilter;
                      return matchSearch && matchType;
                    })
                    .sort((a, b) => a.type.localeCompare(b.type));

                  const totalPages = Math.max(1, Math.ceil(filtered.length / rowsPerPage));
                  const paginated = filtered.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

                  return (
                    <>
                      <div className="sec-table-body">
                        {paginated.length === 0 ? (
                          <div style={{ textAlign: 'center', padding: '32px', color: '#94A3B8', fontSize: '14px' }}>
                            No sections match your search or filter.
                          </div>
                        ) : paginated.map((row) => (
                          <div key={row.id} className="sec-table-row">
                            <span className="sec-cell-name">{row.name}</span>
                            <span className="sec-cell-desc" title={row.desc}>{row.desc}</span>
                            <span className={`sec-cell-type ${row.type === 'Compulsory' ? 'sec-type-compulsory' : 'sec-type-optional'}`}>
                              {row.type}
                            </span>
                            <span className="sec-cell-questions">{row.questions}</span>
                            {(canEdit || canDelete) && (
                              <div className="sec-cell-actions">
                                {row.isSystem ? (
                                  <span className="sec-system-label">System Section</span>
                                ) : (
                                  <>
                                    {canEdit && (
                                      <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" style={{cursor:'pointer'}} onClick={() => handleEdit(row)}><path d="M17.5781 4.44314L13.5562 0.421888C13.4225 0.288135 13.2637 0.182034 13.089 0.109646C12.9143 0.037258 12.727 0 12.5379 0C12.3488 0 12.1615 0.037258 11.9868 0.109646C11.812 0.182034 11.6533 0.288135 11.5196 0.421888L0.422091 11.5199C0.287798 11.6532 0.181326 11.8118 0.108866 11.9865C0.0364062 12.1613 -0.000597072 12.3487 7.28518e-06 12.5378V16.56C7.28518e-06 16.9419 0.151715 17.3082 0.421757 17.5782C0.691799 17.8483 1.05805 18 1.43995 18H16.5594C16.7503 18 16.9334 17.9241 17.0685 17.7891C17.2035 17.6541 17.2793 17.4709 17.2793 17.28C17.2793 17.089 17.2035 16.9059 17.0685 16.7709C16.9334 16.6358 16.7503 16.56 16.5594 16.56H7.49852L17.5781 6.47986C17.7119 6.34614 17.818 6.18738 17.8904 6.01264C17.9627 5.83791 18 5.65063 18 5.4615C18 5.27236 17.9627 5.08508 17.8904 4.91035C17.818 4.73562 17.7119 4.57686 17.5781 4.44314ZM5.4619 16.56H1.43995V12.5378L9.35965 4.61774L13.3816 8.63989L5.4619 16.56ZM14.3995 7.62197L10.3784 3.59983L12.5383 1.4398L16.5594 5.46195L14.3995 7.62197Z" fill="#3B82F6"/></svg>
                                    )}
                                    {canDelete && (
                                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{cursor:'pointer',marginLeft:'8px'}} onClick={() => { setDeleteError(''); setSectionToDelete(row); }}><path d="M3 6h18"/><path d="M8 6V4h8v2"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/></svg>
                                    )}
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>

                    <div className="qb-pagination">
                      <div className="qb-pagination-pages">
                        <button className="qb-page-nav" onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>
                          <span>{"<<"} Prev</span>
                        </button>
                        <button className="qb-page-nav" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>
                          <span>{"<"} Prev</span>
                        </button>

                        {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                          <button
                            key={p}
                            className={`qb-page-btn ${currentPage === p ? 'active' : ''}`}
                            onClick={() => setCurrentPage(p)}
                          >
                            {p}
                          </button>
                        ))}

                        <button className="qb-page-nav" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>
                          <span>Next {">"}</span>
                        </button>
                        <button className="qb-page-nav" onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}>
                          <span>Next {">>"}</span>
                        </button>
                      </div>

                      <div className="qb-pagination-info">
                        <select
                          className="qb-rows-btn"
                          value={rowsPerPage}
                          onChange={e => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                          style={{ cursor: 'pointer' }}
                        >
                          {[5, 10, 15, 20].map(n => <option key={n} value={n}>{String(n).padStart(2,'0')}</option>)}
                        </select>
                        <span className="qb-pagination-count">Items per page</span>
                      </div>
                    </div>
                    </>
                  );
                })()}

            </div>
          </div>
            </main>
            {/* ── Modal ── */}
        {showModal && (
        <div className="sec-modal-overlay" onClick={handleCloseModalOverlay}>
            <div className="sec-modal" onClick={e => e.stopPropagation()}>

            {/* Close button */}
            <button className="sec-modal-close" onClick={handleCloseModal}>
                <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
                <path d="M10 1L1 10M1 1L10 10" stroke="#94A3B8" strokeWidth="1.84" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
            </button>

            {/* Name field */}
            <div className="sec-modal-field">
                <label className="sec-modal-label">Name<span className="required-star">*</span></label>
                <input className="sec-modal-input" type="text" value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} placeholder="Section name" />
            </div>

            {/* Description field */}
            <div className="sec-modal-field">
                <label className="sec-modal-label">Description</label>
                <input className="sec-modal-input" type="text" value={form.desc} onChange={e => setForm(p => ({...p, desc: e.target.value}))} placeholder="Optional description" />
            </div>

            {/* Buttons */}
            <div className="sec-modal-actions">
                <button className="sec-modal-cancel" onClick={handleCloseModal}>Cancel</button>
                <button className="sec-modal-save" onClick={handleSave}>{editingId ? 'Update' : 'Save'}</button>
            </div>

            </div>
        </div>
        )}

        {sectionToDelete && (
        <div className="sec-confirm-overlay" onClick={() => !deleting && setSectionToDelete(null)}>
            <div className="sec-confirm-dialog" onClick={e => e.stopPropagation()}>
            <button className="sec-confirm-close" type="button" onClick={() => !deleting && setSectionToDelete(null)}>
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M13 1L1 13M1 1L13 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
            </button>
            <div className="sec-confirm-icon">
                <svg width="33" height="33" viewBox="0 0 33 33" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M27.8238 6.18259H5.15258C4.87927 6.18259 4.61716 6.29116 4.4239 6.48442C4.23064 6.67767 4.12207 6.93979 4.12207 7.2131C4.12207 7.4864 4.23064 7.74852 4.4239 7.94178C4.61716 8.13504 4.87927 8.24361 5.15258 8.24361H6.18309V26.7928C6.18309 27.3394 6.40023 27.8636 6.78675 28.2501C7.17326 28.6367 7.69749 28.8538 8.24411 28.8538H24.7323C25.2789 28.8538 25.8031 28.6367 26.1896 28.2501C26.5761 27.8636 26.7933 27.3394 26.7933 26.7928V8.24361H27.8238C28.0971 8.24361 28.3592 8.13504 28.5525 7.94178C28.7457 7.74852 28.8543 7.4864 28.8543 7.2131C28.8543 6.93979 28.7457 6.67767 28.5525 6.48442C28.3592 6.29116 28.0971 6.18259 27.8238 6.18259ZM24.7323 26.7928H8.24411V8.24361H24.7323V26.7928ZM10.3051 3.09106C10.3051 2.81775 10.4137 2.55563 10.607 2.36238C10.8002 2.16912 11.0623 2.06055 11.3356 2.06055H21.6407C21.914 2.06055 22.1762 2.16912 22.3694 2.36238C22.5627 2.55563 22.6712 2.81775 22.6712 3.09106C22.6712 3.36437 22.5627 3.62648 22.3694 3.81974C22.1762 4.013 21.914 4.12157 21.6407 4.12157H11.3356C11.0623 4.12157 10.8002 4.013 10.607 3.81974C10.4137 3.62648 10.3051 3.36437 10.3051 3.09106Z" fill="#DC2626"/>
                </svg>
            </div>
            <div className="sec-confirm-copy">
                <h2>Delete this section?</h2>
                <p>This section will be deleted permanently. You will not be able to access or restore it later.</p>
                {deleteError && <div className="sec-confirm-error">{deleteError}</div>}
            </div>
            <div className="sec-confirm-actions">
                <button className="sec-confirm-btn sec-confirm-cancel" disabled={deleting} onClick={() => setSectionToDelete(null)}>
                Cancel
                </button>
                <button className="sec-confirm-btn sec-confirm-delete" disabled={deleting} onClick={handleDelete}>
                {deleting ? 'Deleting...' : 'Yes, Delete'}
                </button>
            </div>
            </div>
        </div>
        )}
            

    </div>
  );
}
